import {
  AnalyticsService,
  AuthorizationService,
  LoggingService,
  TimePeriod,
  ExpirationTimePeriod,
  TimePeriodUtils,
  UserPermissions,
  UserPermissionsUtils,
  OSType,
} from "app/shared";
import { debounceTime, lastValueFrom, Subject } from "rxjs";
import {
  BaseChartSettings,
  UserAnalyticConfiguration,
} from "app/shared/model/charts/user-analytic-configurations.model";
import { CommonAnalyticsLogic } from "./CommonAnalyticsLogic";
import { TooltipCoordinatorService } from "./services/tooltip-coordinator.service";

export abstract class BaseDetailsLogic<
  TConfig
> extends CommonAnalyticsLogic<TConfig> {
  private userIdChange$ = new Subject<number>();

  userConfigurationObject: UserAnalyticConfiguration | null | undefined;
  showFormButtons: boolean = false;
  originalSettings: string;

  // For Form
  timePeriods: { value: TimePeriod; display: string }[] = Object.values(
    TimePeriod
  ).map((value) => ({
    value,
    display: TimePeriodUtils.getStringForTimePeriod(value as TimePeriod),
  }));

  // Define the specific time periods you want to include
  hourlyTimePeriodsEnums: TimePeriod[] = [
    TimePeriod.OneHour,
    TimePeriod.TwoHours,
    TimePeriod.TwentyFourHours,
    TimePeriod.FortyEightHours,
    TimePeriod.SeventyTwoHours,
    // Add other specific time periods as needed
  ];

  // Filter the enum to include only the specific time periods
  hourlyTimePeriods: { value: TimePeriod; display: string }[] =
    this.hourlyTimePeriodsEnums.map((value) => ({
      value,
      display: TimePeriodUtils.getStringForTimePeriod(value as TimePeriod),
    }));

  // Define the specific time periods you want to include
  nonHourlyTimePeriodsEnums: TimePeriod[] = [
    TimePeriod.OneDay,
    TimePeriod.OneWeek,
    TimePeriod.OneMonth,
    TimePeriod.ThreeMonths,
    TimePeriod.SixMonths,
    TimePeriod.OneYear,
    // Add other specific time periods as needed
  ];

  // Filter the enum to include only the specific time periods
  nonHourlyTimePeriods: { value: TimePeriod; display: string }[] =
    this.nonHourlyTimePeriodsEnums.map((value) => ({
      value,
      display: TimePeriodUtils.getStringForTimePeriod(value as TimePeriod),
    }));

  // For Form
  userPermissions: { value: UserPermissions; display: string }[] =
    Object.values(UserPermissions)
      .filter((value) => typeof value === "number")
      .map((value) => ({
        value,
        display: UserPermissionsUtils.getStringForPermission(
          value as UserPermissions
        ),
      }));

  // /**
  //  * Initialized in base class to handle loading config state, does not apply to all child classes
  //  */
  // selectedCustomLabels: string[] = [];

  expirationTimePeriods: { value: ExpirationTimePeriod; display: string }[] =
    Object.values(ExpirationTimePeriod).map((value) => ({
      value,
      display: TimePeriodUtils.getStringForExpirationTimePeriod(
        value as ExpirationTimePeriod
      ),
    }));

  osTypes: { value: OSType; display: string }[] = Object.values(OSType)
    .filter((value) => typeof value === "string")
    .map((value) => ({
      value,
      display: value,
    }));

  constructor(
    analyticsService: AnalyticsService,
    authService: AuthorizationService,
    loggingService: LoggingService,
    configTypeConstructor: new () => TConfig,
    protected tooltipCoordinator: TooltipCoordinatorService
  ) {
    super(analyticsService, authService, loggingService, configTypeConstructor);

    //debounce call to get user analytics data, prevent extra calls caused by multiple renders of the components
    this.subscriptions.push(
      this.userIdChange$
        .pipe(debounceTime(1000))
        .subscribe((userId: number) => {
          this.loggingService.log(
            "BaseDetailsLogic  - event - userIdChange$ debounced getUserAnalyticsConfigurationsData"
          );
          this.analyticsService.getUserAnalyticsConfigurationsData(userId);
        })
    );

    this.subscriptions.push(
      this.analyticsService.userAnalyticsConfigurationsDataChange.subscribe(
        (data) => {
          this.loggingService.log(
            "BaseDetailsLogic - event - userAnalyticsConfigurationsDataChange: ",
            data
          );
          if (data && Array.isArray(data)) {
            this.updateConfigShared(data);
          } else {
            this.initializeConfig();
          }
        }
      )
    );
  }

  private identifyConfig(
    data: UserAnalyticConfiguration[]
  ): UserAnalyticConfiguration | null {
    const typeName = this.getConfigType().toString();
    return data.find((c) => c.configType == typeName);
  }

  private updateConfigShared(data: UserAnalyticConfiguration[]): void {
    this.loggingService.log("BaseDetailsLogic updateConfigShared: ", data);
    const config = this.identifyConfig(data);
    if (config) {
      this.userConfigurationObject = config;
      this.updateConfig(config);
    } else {
      this.initializeConfig();
    }
  }

  updateConfig(data: UserAnalyticConfiguration): void {
    this.loggingService.log("BaseDetailsLogic updateConfig: ", data);
    this.assignConfigData(data);
    this.originalSettings = JSON.stringify(this.config);
    //this.generalSettings = this.settings.generalSettings;
    this.isLoading = false;
    this.onConfigReady();
  }
  protected onConfigReady(): void {
    // Can be overridden by child components
  }

  initializeConfig(): void {
    this.loggingService.log("BaseDetailsLogic initializeConfig");
    this.assignConfigData();
    const newConfig = this.getConfigModel(this.config);
    this.SaveChanges(newConfig);
    this.isLoading = false;
  }

  onSaveSettings(): void {
    this.loggingService.log("BaseDetailsLogic - onSaveSettings: ", this.config);
    this.originalSettings = JSON.stringify(this.config); // Clone settings to reset change detection
    this.showFormButtons = false;
    const newConfig = this.getConfigModel(this.config);
    this.SaveChanges(newConfig);
  }

  onRevertSettings(): void {
    this.config = JSON.parse(this.originalSettings);
    //this.generalSettings = this.settings.generalSettings;
    this.userConfigurationObject.config = this.config;
    this.showFormButtons = false;
    const newConfig = this.getConfigModel(this.config);
    this.SaveChanges(newConfig);
  }

  detectSettingsChange(): boolean {
    const isChanged = JSON.stringify(this.config) !== this.originalSettings;
    this.showFormButtons = isChanged;
    this.loggingService.log(
      "BaseDetailsLogic - detectSettingsChange: ",
      isChanged
    );
    return isChanged;
  }

  onGeneralSettingsChange(updatedSettings: BaseChartSettings): void {
    this.loggingService.log(
      "BaseDetailsLogic - onGeneralSettingsChange: ",
      updatedSettings
    );
    this.loggingService.log(
      "BaseDetailsLogic - onGeneralSettingsChange - settings: ",
      this.config
    );
    this.loggingService.log(
      "baseDetailsLogic - onGeneralSettingsChange - userConfigurationObject: ",
      this.userConfigurationObject
    );
    this.detectSettingsChange();
    this.userConfigurationObject.config = this.config;

    this.loggingService.log(
      "BaseDetailsLogic - onGeneralSettingsChange - userConfigurationObject after : ",
      this.userConfigurationObject
    );

    const config: UserAnalyticConfiguration = Object.assign(
      new UserAnalyticConfiguration(),
      this.userConfigurationObject
    );
    this.analyticsService.changeUserAnalyticsConfigurationChartDisplayOptions(
      config
    );
  }

  // Deprecated: This method is not used in the current implementation, was used to toggle columns in sub tables
  // /**
  //  * Used in multi checkbox select inputs
  //  * @param option
  //  * @param column
  //  */
  // toggleCustomLabelOption(option: string, column: string = ""): void {
  //   if (column) {
  //     const index = this.config.customLabelSelection[column].indexOf(option);
  //     if (index > -1) {
  //       this.config.customLabelSelection[column].splice(index, 1); // Deselect
  //     } else {
  //       this.config.customLabelSelection[column].push(option); // Select
  //     }
  //   } else {
  //     const index = this.config.customLabelSelection.indexOf(option);
  //     if (index > -1) {
  //       this.config.customLabelSelection.splice(index, 1); // Deselect
  //     } else {
  //       this.config.customLabelSelection.push(option); // Select
  //     }
  //   }

  //   this.detectSettingsChange();
  // }

  refreshData(): void {
    this.loggingService.log(
      "BaseDetailsLogic - initiate refreshData in service"
    );
    this.analyticsService.refreshDataPoints();
  }

  SaveChanges(config: UserAnalyticConfiguration): void {
    this.loggingService.log("BaseDetailsLogic - SaveChanges: ", config);
    this.isLoading = true;
    this.analyticsService.setUserAnalyticsConfigurationsData(config);
  }

  refreshDataFromFilters(): void {
    // 4-18-25
    // This is logic to re-fetch the data every time a value in 'Filter Data' section changes
    // Uncomment everything below to enable it
    // known bug: this breaks the undo button, as doing this updates the this.config that's used when the undo button is pressed
    //this.loggingService.log("BaseDetailsLogic - refreshDataFromFilters");
    //const config = this.getConfigModel(this.config);
    //this.isLoading = true;
    //this.analyticsService.refetchUserAnalyticsData(config);
  }

  private triggerGetUserAnalyticsConfigurationsData(userId: number) {
    this.loggingService.log(
      "BaseDetailsLogic triggerGetUserAnalyticsConfigurationsData"
    );
    this.userIdChange$.next(userId);
  }

  init(): void {
    this.isLoading = true;
    if (!this.analyticsService.userAnalyticsConfigurationsData) {
      this.triggerGetUserAnalyticsConfigurationsData(this.authService.userId);
    } else {
      this.updateConfigShared([
        ...this.analyticsService.userAnalyticsConfigurationsData,
      ]);
    }
  }

  onInfoClick(evt: MouseEvent, tooltipKey?: string): void {
    evt.stopPropagation();
    
    if (!tooltipKey) {
      const button = evt.target as HTMLButtonElement;
      const titleAttr = button.getAttribute('title');
      tooltipKey = Object.keys(this.getInfoTexts()).find(key => 
        this.info(key) === titleAttr
      );
    }
    
    if (tooltipKey) {
      this.tooltipCoordinator.handleInfoClick(evt, tooltipKey, this.getInfoTexts());
    }
  }

  isTooltipVisible(key: string): boolean {
    return this.tooltipCoordinator.isTooltipVisible(key);
  }

  hideTooltip(): void {
    this.tooltipCoordinator.hideAllTooltips();
  }

  protected abstract getInfoTexts(): { [key: string]: string };

  info(key: string): string {
    const infoTexts = this.getInfoTexts();
    
    if (key === 'labelsChooser' && !infoTexts[key]) {
      return infoTexts['legendLabelsChooser'] || "No description available.";
    }
    
    return infoTexts[key] || "No description available.";
  }
}
